import React, { useState } from 'react';
import { Alert, Button, Modal, Statistic, Tag, Typography, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, ClockCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import MobileModuleHeader from '../mobile/MobileModuleHeader';
import { useAuth } from '../context/AuthContext';
import './audit-workspace.css';

const { Text } = Typography;
const auditThemeVariables = (token) => ({
  '--audit-primary': `var(--color-primary, ${token.colorPrimary})`, '--audit-success': `var(--color-success, ${token.colorSuccess})`,
  '--audit-warning': `var(--color-warning, ${token.colorWarning})`, '--audit-error': `var(--color-danger, ${token.colorError})`,
  '--audit-bg': `var(--bg-primary, ${token.colorBgContainer})`, '--audit-subtle': `var(--bg-tertiary, ${token.colorFillAlter})`,
  '--audit-text': `var(--text-primary, ${token.colorText})`, '--audit-secondary': `var(--text-secondary, ${token.colorTextSecondary})`,
  '--audit-border': `var(--border-color, ${token.colorBorderSecondary})`,
});

export function AuditToolbar({ mobile = false, summary, children }) {
  const [open, setOpen] = useState(false);
  const { token } = theme.useToken();
  if (!mobile) return <div className="audit-toolbar">{children}</div>;
  return <><div className="audit-mobile-filters"><span>{summary}</span><Button aria-label="Filtres" icon={<FilterOutlined />} onClick={() => setOpen(true)}>Filtres</Button></div>
    <Modal title="Filtres" open={open} onCancel={() => setOpen(false)} centered width="calc(100% - 24px)"
      footer={<Button onClick={() => setOpen(false)}>Terminer</Button>}>
      <div className="audit-workspace audit-filter-dialog" style={auditThemeVariables(token)}
        onClick={(event) => { if (event.target.closest?.('[data-audit-apply]')) setOpen(false); }}>
        <div className="audit-toolbar">{children}</div>
      </div>
    </Modal>
  </>;
}

export function AuditDetailContent({ children }) {
  const { token } = theme.useToken();
  return <div className="audit-workspace audit-detail-value" style={auditThemeVariables(token)}>{children}</div>;
}

export const auditActionLabel = (action) => ({
  CREATE: 'Création', UPDATE: 'Modification', DELETE: 'Suppression', READ: 'Consultation',
  VIEW_HISTORY: 'Historique', ARCHIVE: 'Archivage', EXPORT: 'Export', IMPORT: 'Import',
  LOGIN: 'Connexion', LOGOUT: 'Déconnexion', LOGIN_FAILURE: 'Connexion refusée',
  REFUND: 'Remboursement', CANCEL: 'Annulation', PURGE: 'Purge', FORCE_DELETE: 'Suppression définitive',
}[action] || action || '—');

export const auditDateLabel = (value, { seconds = false, short = false, dateOnly = false } = {}) => {
  if (!value) return '—';
  // Les bornes SQL du journal sont des dates UTC sans suffixe ; ne pas les
  // interpréter dans le fuseau du navigateur lors d'un lien depuis le dashboard.
  const source = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/.test(value)
    ? `${value.replace(' ', 'T')}Z` : value;
  const date = new Date(source);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', day: '2-digit', month: '2-digit',
    ...(!short ? { year: 'numeric' } : {}), ...(!dateOnly ? { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) } : {}),
  }).format(date);
};

export const auditFailureReason = (details) => ({
  MISSING_CREDENTIALS: 'Identifiants manquants', USER_NOT_FOUND: 'Utilisateur introuvable',
  WRONG_PASSWORD: 'Mot de passe incorrect', LOGIN_DISABLED: 'Connexion désactivée', USER_INACTIVE: 'Compte inactif',
  COMPANY_DISABLED: 'Entreprise désactivée', TRIAL_EXPIRED: 'Période d’essai expirée',
  USER_INITIATED: 'Déconnexion volontaire', INACTIVITY: 'Inactivité',
}[details?.reason] || details?.reason || '—');

export const auditIdleStatus = (seconds) => {
  if (seconds == null || seconds === '' || !Number.isFinite(Number(seconds)) || Number(seconds) < 0) {
    return { label: 'Activité inconnue', color: 'default' };
  }
  const idle = Number(seconds);
  if (idle < 60) return { label: 'En ligne', color: 'success' };
  const duration = idle < 3600 ? `${Math.floor(idle / 60)} min`
    : `${Math.floor(idle / 3600)} h${Math.floor((idle % 3600) / 60) ? ` ${Math.floor((idle % 3600) / 60)} min` : ''}`;
  return { label: idle < 300 ? `Actif il y a ${duration}` : `Inactif depuis ${duration}`,
    color: idle < 300 ? 'success' : 'warning' };
};

export const isAuditSessionSuperAdmin = (row) => row?.is_superadmin === true || Number(row?.is_superadmin) === 1;

export function AuditStatus({ status }) {
  const config = { success: { label: 'Succès', color: 'success', icon: <CheckCircleOutlined /> },
    failure: { label: 'Échec', color: 'error', icon: <CloseCircleOutlined /> },
    partial: { label: 'Partiel', color: 'warning', icon: <WarningOutlined /> } }[status];
  return config ? <Tag color={config.color} icon={config.icon}>{config.label}</Tag> : <Tag>{status || 'Inconnu'}</Tag>;
}

export function AuditField({ label, htmlFor, children, className = '' }) {
  return <div className={`audit-field ${className}`}><label htmlFor={htmlFor}>{label}</label>{children}</div>;
}

export function AuditMetricStrip({ items }) {
  return <div className="audit-metric-strip" role="group" aria-label="Indicateurs d’audit">
    {items.map(({ label, value, icon, suffix, success, precision }) => <div className={`audit-metric${success ? ' audit-metric--success' : ''}`} key={label}>
      {icon ? <span className="audit-metric-icon" aria-hidden="true">{icon}</span> : null}
      <Statistic title={label} value={value == null ? '—' : value} suffix={suffix} precision={precision} decimalSeparator="," groupSeparator=" " />
    </div>)}
  </div>;
}

export function AuditReadError({ error, hasData, retry }) {
  if (!error) return null;
  return <Alert type="error" showIcon className="audit-read-error"
    message={hasData ? 'Actualisation impossible : les dernières données disponibles sont conservées.' : 'Données indisponibles'}
    description={typeof error === 'string' ? error : error.message}
    action={<Button onClick={retry}>Réessayer</Button>} />;
}

const mobileTabs = [{ path: '/audit/dashboard', label: 'Tableau de bord' }, { path: '/audit/logs', label: 'Journal d’audit' },
  { path: '/audit/connexions', label: 'Connexions' }, { path: '/audit/sessions', label: 'Sessions actives' }];

export default function AuditWorkspace({ title, subtitle, mobile = false, updatedAt, actions, notice, children }) {
  const { token } = theme.useToken();
  const parentContext = useOutletContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canViewTabs = Number(user?.is_superadmin) === 1 || hasPermission?.('view_audit_logs');
  const variables = auditThemeVariables(token);
  return <section className={`audit-workspace${mobile ? ' audit-workspace--mobile' : ''}`} style={variables}>
    {mobile ? <><MobileModuleHeader title={title} subtitle={subtitle} onMenu={parentContext?.openMobileMenu} />
      {canViewTabs ? <nav className="audit-mobile-navigation" aria-label="Onglets d’audit">
        {mobileTabs.map((tab) => <button type="button" key={tab.path} aria-current={location.pathname === tab.path ? 'page' : undefined}
          onClick={() => navigate(tab.path)}>{tab.label}</button>)}
      </nav> : null}</> : null}
    <div className="audit-workspace-content">
      <header className="audit-heading">
        {!mobile ? <div className="audit-heading-copy"><h1>{title}</h1><p>{subtitle}</p></div> : null}
        <div className="audit-heading-meta">
          {updatedAt ? <Text type="secondary"><ClockCircleOutlined /> Mis à jour le {auditDateLabel(updatedAt, { seconds: true })} UTC</Text> : null}
          <div className="audit-heading-actions">{notice}{actions}</div>
        </div>
      </header>
      {children}
    </div>
  </section>;
}
